import { invoiceLineContribution } from "@common/helpers/invoice-line-allocation";
import { isInvoiceItemLine } from "@common/helpers/lineItems";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { InvoiceLineItemKind } from "@common/types/invoice";

export interface SchedulableInvoiceLine {
    id: string;
    amount: number;
    kind: InvoiceLineItemKind;
    rowId: string | null;
}

interface SchedulableInvoiceLineSelection<T extends SchedulableInvoiceLine> {
    lines: readonly T[];
    sourceLineIds?: readonly string[] | null;
}

/**
 * The lines a revenue schedule moves onto its holding account: the item lines
 * in scope (every item when none are selected) plus the discount lines that
 * point at them. A document-level discount (no rowId) belongs to the whole
 * invoice, so only a whole-invoice schedule takes it. Tax is a liability and a
 * deposit is a customer prepayment, so neither is ever revenue to defer.
 */
export function getSchedulableInvoiceLines<T extends SchedulableInvoiceLine>(
    options: SchedulableInvoiceLineSelection<T>,
): T[] {
    const selectedLineIds = new Set(options.sourceLineIds ?? []);
    const isWholeInvoice = selectedLineIds.size === 0;
    const itemLineIds = new Set(
        options.lines
            .filter(
                (line) =>
                    isInvoiceItemLine(line) &&
                    (isWholeInvoice || selectedLineIds.has(line.id)),
            )
            .map((line) => line.id),
    );
    return options.lines.filter((line) => {
        if (itemLineIds.has(line.id)) {
            return true;
        }
        if (line.kind !== InvoiceLineItemKind.DISCOUNT) {
            return false;
        }
        return line.rowId === null
            ? isWholeInvoice
            : itemLineIds.has(line.rowId);
    });
}

/** Revenue a schedule covers: the schedulable items net of their discounts. */
export function getSchedulableInvoiceAmount(
    options: SchedulableInvoiceLineSelection<SchedulableInvoiceLine>,
): number {
    return Math.abs(
        sumMonetaryValues(
            getSchedulableInvoiceLines(options).map((line) =>
                invoiceLineContribution(line),
            ),
        ),
    );
}
